/**
 * HOC to handle the opacity animation
 */
import DeviceConfig from '@devices/config';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Constants
 *
 * DEFAULT_DELAY: The default delay for the opacity animation (0ms)
 * DEFAULT_DURATION: The default duration for the opacity animation (200ms)
 */
const DEFAULT_DELAY = 0;
const DEFAULT_DURATION = 200;

/**
 * Props interface for the withOpacity HOC
 */
type WithOpacityProps = {
  children?: React.ReactNode;
  delay?: number;
  duration?: number;
  focused?: boolean; // When true, opacity is 1; when false, opacity is unfocusedOpacity. When undefined, uses delay-based fade-in instead.
  limits?: {
    min?: number;
    max?: number;
  };
  style?: React.CSSProperties;
  transition?: string;
  [key: string]: unknown;
};

/**
 * HOC to handle the opacity animation.
 * This HOC will apply an opacity animation to the wrapped element.
 *
 * @param Element - The element to wrap
 * @returns {React.ReactNode}
 */
export default function withOpacity(Element: React.ElementType, debug = false) {
  return ({
    children,
    display = true,
    delay,
    duration,
    focused,
    limits,
    style,
    transition,
    $ref,
    ...props
  }: WithOpacityProps & { display?: boolean }) => {
    const delayMs = delay ?? DEFAULT_DELAY;
    const durationMs = duration ?? DEFAULT_DURATION;

    // ------------------------------------------------------------
    // Variables
    // ------------------------------------------------------------
    const [hovered, setHovered] = useState(false);
    const [opacity, setOpacity] = useState(0);

    const { effectiveOpacity, isFocusDriven } = useMemo(() => {
      const isFocusDriven = typeof focused === 'boolean';
      const hoveredOpacity = isFocusDriven ? 1 : opacity;
      const effectiveOpacity = hovered ? hoveredOpacity : opacity;
      return {
        effectiveOpacity,
        isFocusDriven,
      };
    }, [focused, hovered, opacity]);

    const mergedTransition = useMemo(() => {
      const opacityTransition = `opacity ${durationMs}ms ease-out ${delayMs}ms`;
      const existingTransition =
        typeof style === 'object' && style !== null && style.transition
          ? style.transition
          : '';
      return [transition, existingTransition, opacityTransition]
        .filter(Boolean)
        .join(', ');
    }, [durationMs, delayMs, style, transition]);

    // ------------------------------------------------------------
    // Event handlers
    // ------------------------------------------------------------

    // These props are infered from the props, they may not be provided.
    const propsPointerEnter = (
      props as { onPointerEnter?: (e: React.PointerEvent) => void }
    ).onPointerEnter;
    const propsPointerLeave = (
      props as { onPointerLeave?: (e: React.PointerEvent) => void }
    ).onPointerLeave;

    // These handlers manage the hovered state and the pointer enter/leave events.
    const onPointerEnter = useCallback(
      (e: React.PointerEvent) => {
        setHovered(true);
        propsPointerEnter?.(e);
      },
      [propsPointerEnter]
    );
    const onPointerLeave = useCallback(
      (e: React.PointerEvent) => {
        setHovered(false);
        propsPointerLeave?.(e);
      },
      [propsPointerLeave]
    );

    // ------------------------------------------------------------
    // Effect hooks
    // ------------------------------------------------------------

    /**
     * Handles the display of the element.
     * Fade-in is deferred (double rAF) so the initial opacity:0 is painted first,
     * otherwise the transition from 0→1 may not run on first mount.
     */
    useEffect(() => {
      // On animation-disabled devices, skip the rAF fade-in dance entirely
      // and just set the target opacity synchronously.
      if (DeviceConfig.capabilities.disableAnimations) {
        setOpacity(
          display
            ? isFocusDriven && !focused
              ? limits?.min ?? 0.5
              : limits?.max ?? 1
            : limits?.min ?? 0
        );
        return;
      }

      // Fade in: defer so browser paints opacity:0 before we set target opacity
      if (display) {
        let cancelled = false;
        const frame = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cancelled) {
              setOpacity(
                isFocusDriven && !focused
                  ? limits?.min ?? 0.5
                  : limits?.max ?? 1
              );
            }
          });
        });
        return () => {
          cancelled = true;
          cancelAnimationFrame(frame);
        };
      }
      // Fade out: animate opacity to 0, then hide with display:none
      setOpacity(limits?.min ?? 0);
    }, [display, durationMs, focused, isFocusDriven]);

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------

    return (
      <Element
        {...props}
        ref={$ref}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        style={
          DeviceConfig.capabilities.disableAnimations
            ? {
                opacity: effectiveOpacity,
              }
            : {
                opacity: effectiveOpacity,
                transition: mergedTransition,
                WebkitTransition: mergedTransition,
                ...(typeof style === 'object' && style !== null ? style : {}),
              }
        }
      >
        {children}
      </Element>
    );
  };
}
